/** Full Ten chained attack runtime for issue 1335. */

import type { Guarantee } from "../foundation/types.js";
import {
  asData,
  asInstruction,
  createSeparateGuarantee,
  type DataValue,
  type Instruction,
  type SeparateStructure,
} from "../theorems/separate.js";
import {
  createPermitGuarantee,
  grantCapability,
  type Capability,
  type PermitStructure,
} from "../theorems/permit.js";
import {
  createDataOriginGuarantee,
  fromOrigin,
  type DataOriginStructure,
  type ProvenancedData,
} from "../theorems/dataOrigin.js";
import {
  classify,
  createDataReachGuarantee,
  type ClassifiedData,
  type DataReachStructure,
} from "../theorems/dataReach.js";
import {
  createPrecommitGuarantee,
  precommit,
  type PrecommittedEffect,
  type PrecommitStructure,
} from "../theorems/precommit.js";
import {
  allocate,
  consume,
  createResConsumeGuarantee,
  type ResourceBudget,
  type ResourceHandle,
  type ResConsumeStructure,
} from "../theorems/resConsume.js";
import {
  createResReleaseGuarantee,
  managedAllocate,
  using,
  type ManagedResource,
  type ResReleaseStructure,
} from "../theorems/resRelease.js";
import {
  createComposeGuarantee,
  safeCompose,
  type ComposeStructure,
  type TypedOperation,
} from "../theorems/compose.js";
import {
  createInvEstablishGuarantee,
  establish,
  type EstablishedState,
  type InvEstablishStructure,
  type Invariant,
} from "../theorems/invEstablish.js";
import {
  createInvPreserveGuarantee,
  preservingTransition,
  type GuardedTransition,
  type InvPreserveStructure,
} from "../theorems/invPreserve.js";
import {
  verifyAccountSDP,
  verifyAlwaysSDP,
  verifyDecomposeSDP,
  verifyDelegateSDP,
  verifyPartitionSDP,
  verifyRequireSDP,
  verifyTraceSDP,
  type StackingCertificate,
} from "../theorems/compositionality.js";
import {
  CANONICAL_CHAIN_THEOREMS,
  getFullTenChainSpec,
  type ChainTheoremName,
  type FullTenChainStepSpec,
} from "./registry.js";

type Service = "protected-deploy" | "public-read";
type DeployOperation = "deploy" | "read";
type VendorOrigin = "verified-vendor";
type Classification = "secret" | "public";
type Destination = "audit-log" | "secrets-vault";
type BuildWindow = "slot-committed";

interface VendorArtifact {
  readonly name: string;
  readonly digest: string;
}

interface RawArtifact {
  readonly kind: "raw";
  readonly value: string;
}

interface DecodedArtifact {
  readonly kind: "decoded";
  readonly value: string;
}

interface NeutralizedArtifact {
  readonly kind: "neutralized";
  readonly value: string;
}

interface ContainerSlot {
  readonly id: string;
  readonly cpuShares: number;
}

interface BuildJobResult {
  readonly artifactPath: string;
  readonly buildDurationMs: number;
}

interface ArtifactState {
  readonly checksumPresent: boolean;
  readonly signatureValid: boolean;
  readonly sizeOk: boolean;
}

interface AuditLogState {
  readonly entryCount: number;
  readonly hashChainValid: boolean;
}

interface DeploymentState extends ArtifactState, AuditLogState {}

export type FullTenChainGuarantee = Guarantee<unknown, never>;

export interface FullTenRuntimeWitness {
  readonly label: string;
  readonly typeNotation: string;
  readonly value: unknown;
}

export interface FullTenRuntimeStep extends FullTenChainStepSpec {
  readonly guarantee: FullTenChainGuarantee;
  readonly stacking: StackingCertificate;
  readonly witness: FullTenRuntimeWitness;
}

export interface FullTenChainCoverage {
  readonly theoremCount: number;
  readonly generatorCount: number;
  readonly modeCount: number;
}

export interface FullTenChainRuntime {
  readonly id: string;
  readonly label: string;
  readonly agdaSource: string;
  readonly raspDemo: string;
  readonly steps: readonly FullTenRuntimeStep[];
  readonly coverage: FullTenChainCoverage;
  readonly refusals: readonly string[];
}

export interface FullTenChainRuntimeValidation {
  readonly ok: boolean;
  readonly errors: readonly string[];
  readonly theoremNames: readonly ChainTheoremName[];
}

function countUnique(values: readonly string[]): number {
  return new Set(values).size;
}

function createFullTenStructures(): {
  readonly guarantees: Readonly<Record<ChainTheoremName, FullTenChainGuarantee>>;
  readonly stacking: Readonly<Record<ChainTheoremName, StackingCertificate>>;
  readonly witnesses: Readonly<Record<ChainTheoremName, FullTenRuntimeWitness>>;
  readonly refusals: readonly string[];
} {
  const refusals: string[] = [];

  const permitStructure: PermitStructure<Service, DeployOperation> = {
    authoritySource: "deployment-rbac",
    grantCapability,
    revokeCapability: () => undefined,
    validateCapability: () => true,
  };
  const deploymentCapability: Capability<"protected-deploy", "deploy"> = grantCapability(
    "protected-deploy",
    "deploy",
  );
  refusals.push("PERMIT refused external-attacker outside deployment-admin slice");

  const separateStructure: SeparateStructure<string, string> = {
    dataOps: {
      validate: (input) => asData(String(input)),
      transform: (data) => data,
    },
    instructionOps: {
      compile: asInstruction,
      execute: (instruction) => String(instruction),
    },
  };
  const artifactContent: DataValue<string> = asData("package-v1.2.3.tar.gz");
  const deployTemplate: Instruction<string> = asInstruction(
    "#!/bin/sh\nprintf '%s' '{{ARTIFACT}}'",
  );

  const originStructure: DataOriginStructure<VendorArtifact, VendorOrigin> = {
    verifiedOrigins: ["verified-vendor"],
    ingest: fromOrigin,
    verify: (data) => data.origin === "verified-vendor",
  };
  const provenancedArtifact: ProvenancedData<VendorArtifact, VendorOrigin> = originStructure.ingest(
    { name: "package-v1.2.3.tar.gz", digest: "sha256:verified" },
    "verified-vendor",
  );

  const reachStructure: DataReachStructure<Classification, Destination> = {
    policies: [
      { classification: "secret", allowedDestinations: ["secrets-vault"] },
      { classification: "public", allowedDestinations: ["audit-log", "secrets-vault"] },
    ],
    checkReach: (classification, destination) =>
      reachStructure.policies.some(
        (policy) =>
          policy.classification === classification &&
          policy.allowedDestinations.includes(destination),
      ),
  };
  const secretCredentials: ClassifiedData<string, "secret"> = classify(
    "sk-demo-not-a-secret-ABC123XYZ",
    "secret",
  );

  const precommitStructure: PrecommitStructure<BuildWindow> = {
    allowedGrades: ["slot-committed"],
    gradeOrder: () => false,
  };
  const buildJobEffect: PrecommittedEffect<BuildWindow, BuildJobResult> = precommit(
    "slot-committed",
    () => ({ artifactPath: "/build/output/app.tar.gz", buildDurationMs: 12000 }),
  );
  const buildJob = buildJobEffect.execute();

  const containerBudget: ResourceBudget = { limit: 1, consumed: 0, remaining: 1 };
  const consumedContainers = new Set<string>();
  const resConsumeStructure: ResConsumeStructure = {
    budget: containerBudget,
    trackConsumption: (handleId) => {
      consumedContainers.add(handleId);
    },
  };
  const containerHandle: ResourceHandle<ContainerSlot> = allocate("container-001", {
    id: "container-001",
    cpuShares: 1000,
  });
  resConsumeStructure.trackConsumption("container-001");
  const consumedContainer = consume(containerHandle);

  const trackedResources = new Set<string>(["container-managed-001"]);
  const resReleaseStructure: ResReleaseStructure = {
    trackedResources,
    verifyAllReleased: () => trackedResources.size === 0,
  };
  const managedContainer: ManagedResource<ContainerSlot> = managedAllocate(
    "container-managed-001",
    { id: "container-managed-001", cpuShares: 1000 },
  );
  const releasedContainer = using(managedContainer, (container) => {
    trackedResources.delete(container.id);
    return container;
  });

  const decodeOp: TypedOperation<RawArtifact, DecodedArtifact> = {
    inputType: "RawArtifact",
    outputType: "DecodedArtifact",
    apply: (input) => ({ kind: "decoded", value: decodeURIComponent(input.value) }),
  };
  const neutralizeOp: TypedOperation<DecodedArtifact, NeutralizedArtifact> = {
    inputType: "DecodedArtifact",
    outputType: "NeutralizedArtifact",
    apply: (input) => ({
      kind: "neutralized",
      value: input.value
        .replace(/\\/g, "")
        .replace(/[;|&`$]/g, "")
        .replace(/\.\./g, ""),
    }),
  };
  const safeBuildPipeline = safeCompose(decodeOp, neutralizeOp);
  const neutralizedArtifact = safeBuildPipeline.apply({ kind: "raw", value: "%3B rm -rf / %3B" });
  const composeStructure: ComposeStructure = {
    operations: [],
    validateComposition: (first, second) =>
      first === decodeOp.outputType && second === neutralizeOp.inputType,
  };

  const deploymentInvariants: readonly Invariant<DeploymentState>[] = [
    { name: "checksum-present", check: (state) => state.checksumPresent },
    { name: "signature-valid", check: (state) => state.signatureValid },
    { name: "size-in-range", check: (state) => state.sizeOk },
    { name: "hash-chain-valid", check: (state) => state.hashChainValid },
    { name: "entries-nonnegative", check: (state) => state.entryCount >= 0 },
  ];
  const initialDeploymentState: DeploymentState = {
    checksumPresent: true,
    signatureValid: true,
    sizeOk: true,
    entryCount: 42,
    hashChainValid: true,
  };
  const invEstablishStructure: InvEstablishStructure<DeploymentState> = {
    invariants: deploymentInvariants,
    initialState: initialDeploymentState,
  };
  const establishedArtifact: EstablishedState<DeploymentState> = establish(
    invEstablishStructure.initialState,
    deploymentInvariants,
  );

  const appendTransition: GuardedTransition<DeploymentState> = {
    name: "append-entry",
    transition: (state) => ({ ...state, entryCount: state.entryCount + 1, hashChainValid: true }),
  };
  const invPreserveStructure: InvPreserveStructure<DeploymentState> = {
    invariants: deploymentInvariants,
    transitions: [appendTransition],
  };
  const preservedAuditState = preservingTransition(
    establishedArtifact,
    appendTransition,
    deploymentInvariants,
  );

  const guarantees = {
    PERMIT: createPermitGuarantee(permitStructure),
    SEPARATE: createSeparateGuarantee(separateStructure),
    "DATA.ORIGIN": createDataOriginGuarantee(originStructure),
    "DATA.REACH": createDataReachGuarantee(reachStructure),
    PRECOMMIT: createPrecommitGuarantee(precommitStructure),
    "RES.CONSUME": createResConsumeGuarantee(resConsumeStructure),
    "RES.RELEASE": createResReleaseGuarantee(resReleaseStructure),
    COMPOSE: createComposeGuarantee(composeStructure),
    "INV.ESTABLISH": createInvEstablishGuarantee(invEstablishStructure),
    "INV.PRESERVE": createInvPreserveGuarantee(invPreserveStructure),
  } satisfies Record<ChainTheoremName, FullTenChainGuarantee>;

  const trace = verifyTraceSDP(originStructure, reachStructure);
  const account = verifyAccountSDP(resConsumeStructure, resReleaseStructure);
  const always = verifyAlwaysSDP(invEstablishStructure, invPreserveStructure);
  const stacking = {
    PERMIT: verifyDelegateSDP(permitStructure),
    SEPARATE: verifyPartitionSDP(separateStructure),
    "DATA.ORIGIN": trace,
    "DATA.REACH": trace,
    PRECOMMIT: verifyRequireSDP(precommitStructure),
    "RES.CONSUME": account,
    "RES.RELEASE": account,
    COMPOSE: verifyDecomposeSDP(composeStructure),
    "INV.ESTABLISH": always,
    "INV.PRESERVE": always,
  } satisfies Record<ChainTheoremName, StackingCertificate>;

  const witnesses = {
    PERMIT: {
      label: "Authority witness",
      typeNotation: "Capability<protected-deploy, deploy>",
      value: deploymentCapability,
    },
    SEPARATE: {
      label: "Boundary witness",
      typeNotation: "DataValue<string> x Instruction<string>",
      value: { artifactContent, deployTemplate },
    },
    "DATA.ORIGIN": {
      label: "Origin witness",
      typeNotation: "ProvenancedData<VendorArtifact, verified-vendor>",
      value: provenancedArtifact,
    },
    "DATA.REACH": {
      label: "Reach witness",
      typeNotation: "ClassifiedData<string, secret>",
      value: secretCredentials,
    },
    PRECOMMIT: {
      label: "Commitment witness",
      typeNotation: "PrecommittedEffect<slot-committed, BuildJobResult>",
      value: { grade: buildJobEffect.grade, result: buildJob },
    },
    "RES.CONSUME": {
      label: "Consumption witness",
      typeNotation: "ResourceHandle<ContainerSlot>",
      value: { consumed: consumedContainer, consumedCount: consumedContainers.size },
    },
    "RES.RELEASE": {
      label: "Release witness",
      typeNotation: "ManagedResource<ContainerSlot>",
      value: { released: releasedContainer, allReleased: resReleaseStructure.verifyAllReleased() },
    },
    COMPOSE: {
      label: "Composition witness",
      typeNotation: "CompositionCertificate<RawArtifact, DecodedArtifact, NeutralizedArtifact>",
      value: { certificate: safeBuildPipeline.certificate, output: neutralizedArtifact },
    },
    "INV.ESTABLISH": {
      label: "Establishment witness",
      typeNotation: "EstablishedState<DeploymentState>",
      value: establishedArtifact,
    },
    "INV.PRESERVE": {
      label: "Preservation witness",
      typeNotation: "EstablishedState<DeploymentState>",
      value: preservedAuditState,
    },
  } satisfies Record<ChainTheoremName, FullTenRuntimeWitness>;

  return { guarantees, stacking, witnesses, refusals };
}

export function createFullTenChainRuntime(): FullTenChainRuntime {
  const spec = getFullTenChainSpec();
  const { guarantees, stacking, witnesses, refusals } = createFullTenStructures();
  const steps = spec.steps.map((step): FullTenRuntimeStep => {
    const theorem = step.theorem;
    return {
      ...step,
      guarantee: guarantees[theorem],
      stacking: stacking[theorem],
      witness: witnesses[theorem],
    };
  });
  const meta = steps.map((step) => step.guarantee.meta);
  return {
    id: spec.id,
    label: spec.label,
    agdaSource: spec.agdaSource,
    raspDemo: spec.raspDemo,
    steps,
    coverage: {
      theoremCount: countUnique(steps.map((step) => step.theorem)),
      generatorCount: countUnique(meta.map((entry) => entry.generator)),
      modeCount: countUnique(meta.map((entry) => entry.mode)),
    },
    refusals,
  };
}

export function validateFullTenChainRuntime(
  runtime: FullTenChainRuntime = createFullTenChainRuntime(),
): FullTenChainRuntimeValidation {
  const errors: string[] = [];
  const theoremNames = runtime.steps.map((step) => step.theorem);
  if (
    theoremNames.length !== CANONICAL_CHAIN_THEOREMS.length ||
    !theoremNames.every((theorem, index) => theorem === CANONICAL_CHAIN_THEOREMS[index])
  ) {
    errors.push("Full Ten runtime theorem order drifted from issue-1335 canonical order");
  }
  for (const step of runtime.steps) {
    if (step.guarantee.meta.name !== step.theorem) {
      errors.push(
        `${step.id} guarantee meta is ${step.guarantee.meta.name}, expected ${step.theorem}`,
      );
    }
  }
  if (runtime.coverage.theoremCount !== 10)
    errors.push("Full Ten runtime does not cover 10 Neutralization Theorems");
  if (runtime.coverage.generatorCount !== 4)
    errors.push("Full Ten runtime does not cover Forbidden Generators G1-G4");
  if (runtime.coverage.modeCount !== 3)
    errors.push("Full Ten runtime does not cover all three neutralization modes");
  return { ok: errors.length === 0, errors, theoremNames };
}
